import { mdiRefresh } from "@mdi/js";
import { MiBuildPageHeader, MiButton, MiList } from "@miplatform/components";
import {
  BUTTON_VARIANTS,
  DATE_TIME_FORMATS,
  PAGINATION,
  PERMISSIONS,
  SIGNALR_GROUP_NAMES,
} from "@miplatform/constants";
import {
  formatDate,
  getDateWithAddedTime,
  getDurationInHours,
  getNestedValue,
} from "@miplatform/utils";
import { Box, Typography } from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import { usePermissions } from "../../hooks";
import { useGlobalSignalrGroupsStore } from "../../stores/globalSignalrGroupsStore/useGlobalSignalrGroupsStore";
import { CloseoutDrawer } from "../Schedule/Scheduler/CloseoutDrawer/CloseoutDrawer";
import {
  DispatchEmailDrawer,
  useDispatchEmailDrawer,
} from "../Schedule/Scheduler/CloseoutDrawer/Tabs/Issues/Closingout/components/DispatchEmailDrawer";
import { CLOSEOUT_SCREENS } from "../Schedule/Scheduler/CloseoutDrawer/constants";
import { useDispatchVerificationReviewViewModel } from "./useDispatchVerificationReviewViewModel";

const DispatchVerificationReview = () => {
  const { hasPermission } = usePermissions();
  const { classes, theme } = useStyles();
  const [selectedCloseout, setSelectedCloseout] = useState(null);
  const [refresh, setRefresh] = useState(false);
  const { attempts, getAttempts, isLoading, setAttempts } =
    useDispatchVerificationReviewViewModel();

  const handleCloseoutDrawerClose = useCallback(
    () => setSelectedCloseout(null),
    []
  );

  const handleRefresh = () => setRefresh((previous) => !previous);
  
  const count = useGlobalSignalrGroupsStore((state) => state.groups[SIGNALR_GROUP_NAMES.MiDispatch_VerificationReviewCount]?.result);

  useEffect(() => {
    handleRefresh();
  }, [count]); 

  const handleCloseoutDrawerOpen = useCallback(
    (attempt, { screen = CLOSEOUT_SCREENS.MAIN } = {}) => {
      const durationStart = attempt.startDate || attempt.scheduledStartDate;
      const durationEnd =
        attempt.endDate ||
        attempt.scheduledEndDate ||
        (durationStart &&
          getDateWithAddedTime(durationStart, {
            m: attempt.expectedDuration,
          }));

      setSelectedCloseout({
        attempt: {
          ...attempt,
          duration:
            durationStart && durationEnd
              ? getDurationInHours(durationStart, durationEnd)
              : 0,
          exactExtendedTime: attempt.exactExtendedTime ?? 0,
        },
        screen,
      });
    },
    []
  );

  const { emailDrawersMap, handleEmailDrawerOpen, handleEmailDrawerClose } =
    useDispatchEmailDrawer({ onOpen: handleCloseoutDrawerClose });

  const handleReviewOrder = (attempt) => handleCloseoutDrawerOpen(attempt);

  const renderItemActions = (attempt) => (
    <MiButton
      title="Review Order"
      variant={BUTTON_VARIANTS.CONTAINED}
      size="small"
      onClick={(event) => {
        event.stopPropagation();
        handleReviewOrder(attempt);
      }}
    />
  );

  if (!hasPermission(PERMISSIONS.ATTEMPT.REVIEW)) {
    return <Navigate to="/dispatch" replace />;
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.mainContent}>
        <MiBuildPageHeader
          title="Verification Review"
          hasDivider={false}
          actionComponent={
            <MiButton
              title="Refresh"
              variant={BUTTON_VARIANTS.OUTLINED}
              startIcon={{ path: mdiRefresh, iconSize: 0.7 }}
              data-testid="refresh-dispatch-verification-review-btn"
              onClick={handleRefresh}
            />
          }
        />
        {/* <MiTab
          tabs={[{ label: "QC Camera", value: "all" }]}
          currentTab="all"
          onTabChange={() => {}}
          scrollButtons={false}
          chipStyle={{
            height: 18,
            minWidth: 18,
            width: "fit-content",
            borderRadius: "100%",
            "& .MuiChip-label": {
              ...theme.typography.body2,
              fontSize: theme.fontSize.xsmall,
              padding: theme.spacing(0, 1),
            },
          }}
          borderBottom
        /> */}
        <Box className={classes.reviewContent}>
          <Box
            style={{
              backgroundColor: theme.palette.common.white,
              margin: theme.spacing(2),
              borderRadius: theme.shape.borderRadius,
              overflowY: "auto",
              flex: 1,
              minHeight: 0,
            }}
          >
            <MiList
              data={attempts}
              dataProvider={getAttempts}
              loading={isLoading}
              paginationMode={PAGINATION.SERVER}
              defaultPageSize={25}
              setData={setAttempts}
              refresh={refresh}
              viewType="card"
              cardProps={{
                renderItemActions,
                sections: [
                  {
                    fieldset: [
                      {
                        field: "workOrder",
                        renderValue: (row) => {
                          return (
                            <Box>
                              <Typography
                                fontSize={14}
                                color="primary"
                                fontWeight="600"
                              >
                                {row.home?.address?.addressLine1 || "--"}
                              </Typography>
                              <Typography style={{ fontSize: 11 }}>
                                {[
                                  row.home?.address?.city,
                                  row.home?.communityName,
                                  row.home?.builderName,
                                ]
                                  .filter(Boolean)
                                  .join(" | ") || "--"}
                              </Typography>
                            </Box>
                          );
                        },
                      },
                      {
                        label: "Order Type",
                        renderValue: (row) =>
                          getNestedValue(
                            row,
                            "assignment.order.orderTemplateName"
                          ),
                      },
                      {
                        label: "Scheduled Date",
                        field: "startDate",
                        renderValue: (row) =>
                          row.startDate
                            ? formatDate(
                                row.startDate,
                                DATE_TIME_FORMATS.FORMAT_MMM_D_YYYY_H_MM_A
                              )
                            : formatDate(
                                row.scheduledStartDate,
                                DATE_TIME_FORMATS.FORMAT_MMM_D_YYYY_H_MM_A
                              ) || "--",
                      },
                      { label: "Assignee", field: "assignedUserName" },
                    ],
                  },
                ],
              }}
              emptyListProps={{ text: "Nothing to review!" }}
            />
          </Box>
          {selectedCloseout && (
            <Box className={classes.closeoutDrawerOverlay}>
              <CloseoutDrawer
                key={selectedCloseout.attempt.id}
                open
                listAttempt={selectedCloseout.attempt}
                screen={selectedCloseout.screen}
                verificationReview
                onClose={handleCloseoutDrawerClose}
                onAttemptUpdated={handleRefresh}
                onEmailDrawerOpen={handleEmailDrawerOpen}
              />
            </Box>
          )}
          {Object.keys(emailDrawersMap).length > 0 && (
            <Box className={classes.closeoutDrawerOverlay}>
              <DispatchEmailDrawer
                emailDrawersMap={emailDrawersMap}
                onCloseoutDrawerOpen={handleCloseoutDrawerOpen}
                onEmailDrawerClose={handleEmailDrawerClose}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default DispatchVerificationReview;

const useStyles = makeStyles()((theme) => ({
  container: {
    backgroundColor: theme.palette.grey.lightAccent,
    borderTop: `1px solid ${theme.palette.grey.accent}`,
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  closeoutDrawerOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    justifyContent: "flex-end",
  },
  loadingContainer: {
    height: "100%",
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: theme.palette.common.white,
  },
  mainContent: {
    flex: 1,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  reviewContent: {
    flex: 1,
    display: "flex",
    minHeight: 0,
    position: "relative",
  },
}));
